import React, { useState, useEffect } from 'react';
import { X, Sparkles, AlertTriangle, Info, Gift, ExternalLink, Loader2, Check } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { broadcastAllowedOnCurrentPage } from '@/utils/broadcastFilter';


import { useTranslation } from 'react-i18next';


const STYLE_CONFIG = {
  info: { icon: Info, color: '#6366f1', glow: 'rgba(99,102,241,0.15)' },
  feature: { icon: Sparkles, color: '#22c55e', glow: 'rgba(34,197,94,0.15)' },
  promo: { icon: Gift, color: '#f59e0b', glow: 'rgba(245,158,11,0.15)' },
  warning: { icon: AlertTriangle, color: '#ef4444', glow: 'rgba(239,68,68,0.15)' },
};

export default function BroadcastModal() {
  const {
    t
  } = useTranslation();

  const [broadcasts, setBroadcasts] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useTheme();
  const isLight = colorMode === 'light';

  // Don't show on admin or affiliate pages
  const isAdminPage = location.pathname.startsWith('/admin');
  const isAffiliatePage = location.pathname.startsWith('/affiliate');

  useEffect(() => {
    if (isAdminPage || isAffiliatePage) return;
    const fetchBroadcasts = async () => {
      try {
        const res = await axios.get(`${API}/broadcasts/active`, { withCredentials: true });
        if (res.data.broadcasts?.length > 0) {
          // Only show broadcasts whose display_locations include "modal".
          // Legacy broadcasts predating this field (no `display_locations`
          // key at all) default to modal for backward compatibility.
          // Broadcasts explicitly stored with an empty array `[]` are
          // push-only and MUST NOT render in-app.
          const modalBroadcasts = res.data.broadcasts.filter(b => {
            const locs = Array.isArray(b.display_locations) ? b.display_locations : ['modal'];
            if (!locs.includes('modal')) return false;
            // Page-scoping (Feb 2026): admins can now target specific
            // pages. An empty/missing `display_pages` OR the '*'
            // wildcard means "everywhere" (legacy behaviour).
            return broadcastAllowedOnCurrentPage(b, location.pathname);
          });
          setBroadcasts(modalBroadcasts);
        }
      } catch {}
    };
    const timer = setTimeout(fetchBroadcasts, 1200);
    return () => clearTimeout(timer);
  }, [isAdminPage, isAffiliatePage, location.pathname]);

  const dismiss = async (broadcastId) => {
    try {
      await axios.post(`${API}/broadcasts/${broadcastId}/dismiss`, {}, { withCredentials: true });
    } catch {}
    setBroadcasts(prev => prev.filter(b => b.broadcast_id !== broadcastId));
  };

  const handleCta = async (broadcast) => {
    // If broadcast has a trial offer, accept it first
    if (broadcast.offer_tier && broadcast.offer_duration_days) {
      setAccepting(true);
      try {
        const res = await axios.post(`${API}/broadcasts/${broadcast.broadcast_id}/accept`, {}, { withCredentials: true });
        if (res.data.trial_provisioned) {
          toast.success(`Your ${res.data.duration_days}-day ${res.data.tier} trial is now active!`);
          window.dispatchEvent(new Event('subscription-changed'));
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not activate offer');
      } finally {
        setAccepting(false);
      }
      setBroadcasts(prev => prev.filter(b => b.broadcast_id !== broadcast.broadcast_id));
      return;
    }

    // Normal CTA behavior
    if (broadcast.cta_link) {
      if (broadcast.cta_link.startsWith('http')) {
        window.open(broadcast.cta_link, '_blank');
      } else {
        navigate(broadcast.cta_link);
      }
    }
    try {
      await axios.post(`${API}/broadcasts/${broadcast.broadcast_id}/accept`, {}, { withCredentials: true });
    } catch {}
    setBroadcasts(prev => prev.filter(b => b.broadcast_id !== broadcast.broadcast_id));
  };

  if (isAdminPage || isAffiliatePage || broadcasts.length === 0) return null;
  const b = broadcasts[currentIdx] || broadcasts[0];
  if (!b) return null;

  const style = STYLE_CONFIG[b.style] || STYLE_CONFIG.info;
  const Icon = style.icon;

  // Theme-aware colors
  const cardBg = isLight ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.88)';
  const overlayBg = isLight ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.5)';
  const titleColor = isLight ? '#0f172a' : '#ffffff';
  const messageColor = isLight ? '#475569' : '#cbd5e1';
  const closeColor = isLight ? '#94a3b8' : '#94a3b8';
  const closeHover = isLight ? '#0f172a' : '#ffffff';
  const dotActive = isLight ? '#0f172a' : '#ffffff';
  const dotInactive = isLight ? '#cbd5e1' : '#475569';
  const offerBg = isLight ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.1)';
  const offerBorder = isLight ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.2)';
  const offerText = isLight ? '#059669' : '#34d399';
  const cardShadow = isLight
    ? `0 0 40px ${style.glow}, 0 25px 50px rgba(0,0,0,0.12)`
    : `0 0 60px ${style.glow}, 0 25px 50px rgba(0,0,0,0.4)`;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: overlayBg, backdropFilter: 'blur(4px)' }}
      onClick={(e) => e.target === e.currentTarget && dismiss(b.broadcast_id)}
      data-testid="broadcast-user-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 relative animate-in fade-in zoom-in-95 duration-300"
        style={{
          background: cardBg,
          backdropFilter: 'blur(24px)',
          border: `1px solid ${style.color}30`,
          boxShadow: cardShadow,
        }}
      >
        {/* Close */}
        <button
          onClick={() => dismiss(b.broadcast_id)}
          className="absolute top-4 right-4 p-1 rounded-full transition"
          style={{ color: closeColor }}
          onMouseEnter={e => e.currentTarget.style.color = closeHover}
          onMouseLeave={e => e.currentTarget.style.color = closeColor}
          data-testid="broadcast-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon + Title */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${style.color}18` }}
          >
            <Icon className="h-5 w-5" style={{ color: style.color }} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="text-base font-semibold leading-tight" style={{ color: titleColor }}>{b.title}</h3>
          </div>
        </div>

        {/* Message */}
        {b.message && <p className="text-sm leading-relaxed mb-5" style={{ color: messageColor }}>{b.message}</p>}

        {/* Offer badge */}
        {b.offer_tier && b.offer_duration_days && (
          <div className="rounded-xl px-3 py-2 text-xs text-center mb-3" style={{ background: offerBg, border: `1px solid ${offerBorder}`, color: offerText }}>
            <Gift className="h-3.5 w-3.5 inline mr-1.5" />
            {b.offer_duration_days}-day {b.offer_tier} {t('generated.broadcastmodal_trial_included_8330')}
          </div>
        )}

        {/* CTA */}
        {b.cta_text && (
          <button
            onClick={() => handleCta(b)}
            disabled={accepting}
            className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
            style={{ background: style.color }}
            data-testid="broadcast-cta-btn"
          >
            {accepting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : b.offer_tier ? <Check className="h-3.5 w-3.5" /> : null}
            {accepting ? 'Activating...' : b.cta_text}
            {!accepting && b.cta_link?.startsWith('http') && !b.offer_tier && <ExternalLink className="h-3.5 w-3.5" />}
          </button>
        )}

        {/* Multiple broadcasts indicator */}
        {broadcasts.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {broadcasts.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === currentIdx ? '16px' : '6px',
                  background: i === currentIdx ? dotActive : dotInactive,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
